import { uniq } from 'lodash';

import {
  type SelectedMakeModel,
  DEFAULT_MAKE_MODEL_GRAPHQL_KEY_MAP,
} from 'components/DataTable/customFilters/AssetFilter/MakeModelNestedFilter/utils';
import { type AtlasGqlTableView } from 'types/atlas-graphql';

import { type DefectPatternAssetFilter, getFilterValues } from './getRelevantDefectPatterns';

export type DefectPatternFilters = {
  siteIds: string[];
  bladeMakeModels: SelectedMakeModel[];
};

const { componentMakes: BLADE_MAKE_KEY, componentModels: BLADE_MODEL_KEY } =
  DEFAULT_MAKE_MODEL_GRAPHQL_KEY_MAP;

// Narrows a Defect Pattern's saved view to the given Sites and Blade make/models, or returns null
// when it can't match them. The model filter is merged into the view's own Blade model entry:
// `getFilterBy` joins entries with the same key into one list, so a second `componentModel`
// entry would add to the pattern's models instead of narrowing them.
export const scopeDefectPatternView = (
  view: AtlasGqlTableView,
  { siteIds, bladeMakeModels }: DefectPatternFilters
): AtlasGqlTableView | null => {
  if (!siteIds.length && !bladeMakeModels.length) {
    return view;
  }

  const assetFilters: DefectPatternAssetFilter[] = view.filters?.asset ?? [];
  let scopedAssetFilters = assetFilters;

  if (bladeMakeModels.length) {
    const viewModels = getFilterValues(view, BLADE_MODEL_KEY);
    // A Turbine-matched pattern names no Blade model, so every selected make/model applies.
    const overlap = bladeMakeModels.filter(
      ({ model }) => !viewModels.length || viewModels.includes(model)
    );
    if (!overlap.length) {
      return null;
    }

    scopedAssetFilters = [
      ...assetFilters.filter(({ key }) => key !== BLADE_MODEL_KEY),
      { key: BLADE_MAKE_KEY, value: uniq(overlap.map(({ make }) => make)) },
      { key: BLADE_MODEL_KEY, value: uniq(overlap.map(({ model }) => model)) },
    ];
  }

  return {
    ...view,
    filters: {
      ...view.filters,
      asset: scopedAssetFilters,
      ...(siteIds.length ? { site: siteIds } : {}),
    },
  };
};
